/** @format */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { Component, type ErrorInfo, type ReactNode } from "react";

import ErrorBoundary from "@/utils/ErrorBoundary";
import InlineErrorBoundary from "@/utils/InlineErrorBoundary";

// Only accessible in development
export const Route = createFileRoute("/dev/error-boundaries")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: ErrorBoundaryPreview,
});

/** Throws an error on render for testing real error boundaries. */
class ErrorThrower extends Component<
  { children?: ReactNode },
  { shouldThrow: boolean }
> {
  constructor(props: { children?: ReactNode }) {
    super(props);
    this.state = { shouldThrow: false };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Let InlineErrorBoundary handle it
  }

  render() {
    if (this.state.shouldThrow) {
      throw new Error("Test error thrown by ErrorThrower component");
    }
    return (
      <button
        type="button"
        className="text-sm bg-red-600 hover:bg-red-500 text-white rounded px-3 py-1.5"
        onClick={() => this.setState({ shouldThrow: true })}
      >
        Trigger real error
      </button>
    );
  }
}

function mockAxiosError(url: string, status: number, statusText: string): Error {
  const error = new Error(`Request failed with status code ${status}`);
  error.name = "AxiosError";
  Object.assign(error, {
    config: { url, method: "get" },
    response: { status, statusText },
  });
  return error;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-slate-200 mb-3 border-b border-slate-700 pb-2">
        {title}
      </h2>
      {children}
    </div>
  );
}

function ErrorBoundaryPreview() {
  const renderError = new TypeError(
    "Cannot read properties of undefined (reading 'map')"
  );
  const networkError = mockAxiosError(
    "/api/runs/123/vehicles/",
    500,
    "Internal Server Error"
  );
  const network404 = mockAxiosError("/api/races/456/", 404, "Not Found");
  const fetchError = new TypeError("Failed to fetch");

  const componentStack = `
    at RunTable (RunTable.tsx:42)
    at WidgetRenderer (GridComponent.tsx:41)
    at GridComponent (GridComponent.tsx:185)
    at Run (Run.tsx:60)`;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-slate-100 mb-2">Error Boundary Preview</h1>
      <p className="text-slate-400 mb-8 text-sm">
        Dev-only page showing all error boundary variants.
      </p>

      {/* Full-page: Render error (non-staff) */}
      <Section title="Full Page — Render Error">
        <div className="relative h-[450px] bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
          <ErrorBoundary
            error={renderError}
            componentStack={componentStack}
            eventId="abc123def456"
            resetError={() => {}}
          />
        </div>
      </Section>

      {/* Full-page: Network error */}
      <Section title="Full Page — Network Error (500)">
        <div className="relative h-[350px] bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
          <ErrorBoundary
            error={networkError}
            componentStack={componentStack}
            eventId="net500event"
            resetError={() => {}}
          />
        </div>
      </Section>

      {/* Full-page: Network 404 */}
      <Section title="Full Page — Network Error (404)">
        <div className="relative h-[350px] bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
          <ErrorBoundary
            error={network404}
            componentStack={componentStack}
            resetError={() => {}}
          />
        </div>
      </Section>

      {/* Full-page: Fetch failed (no URL/status) */}
      <Section title="Full Page — Fetch Failed (no details)">
        <div className="relative h-[300px] bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
          <ErrorBoundary error={fetchError} resetError={() => {}} />
        </div>
      </Section>

      {/* Full-page: Chunk load error */}
      <Section title="Full Page — Chunk Load Error">
        <div className="relative h-[250px] bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
          <ErrorBoundary
            error={Object.assign(new Error("Loading chunk abc123 failed"), {
              name: "ChunkLoadError",
            })}
            resetError={() => {}}
          />
        </div>
      </Section>

      {/* Inline variants */}
      <Section title="Inline Error Boundaries">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Default */}
          <div className="bg-slate-950 rounded-lg border border-slate-800 p-4">
            <p className="text-xs text-slate-500 mb-2">Default (no name)</p>
            <InlineErrorBoundary>
              <ErrorThrower />
            </InlineErrorBoundary>
          </div>

          {/* With name */}
          <div className="bg-slate-950 rounded-lg border border-slate-800 p-4">
            <p className="text-xs text-slate-500 mb-2">With name prop</p>
            <InlineErrorBoundary name="LeaderboardWidget">
              <ErrorThrower />
            </InlineErrorBoundary>
          </div>

          {/* Custom fallback */}
          <div className="bg-slate-950 rounded-lg border border-slate-800 p-4">
            <p className="text-xs text-slate-500 mb-2">Custom fallback</p>
            <InlineErrorBoundary
              fallback={
                <div className="bg-amber-900/20 border border-amber-800/50 rounded-lg p-4 text-center">
                  <p className="text-amber-400 text-sm">Custom fallback UI</p>
                </div>
              }
            >
              <ErrorThrower />
            </InlineErrorBoundary>
          </div>
        </div>
      </Section>

      {/* No error state */}
      <Section title="No Error (healthy children)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-slate-950 rounded-lg border border-slate-800 p-4">
            <p className="text-xs text-slate-500 mb-2">ErrorBoundary with children</p>
            <ErrorBoundary>
              <p className="text-green-400 text-sm">
                Healthy child content rendered normally.
              </p>
            </ErrorBoundary>
          </div>
          <div className="bg-slate-950 rounded-lg border border-slate-800 p-4">
            <p className="text-xs text-slate-500 mb-2">
              InlineErrorBoundary with children
            </p>
            <InlineErrorBoundary name="TestWidget">
              <p className="text-green-400 text-sm">
                Healthy child content rendered normally.
              </p>
            </InlineErrorBoundary>
          </div>
        </div>
      </Section>
    </div>
  );
}
